import { makeAutoObservable, runInAction } from 'mobx'

import { DiskContentUrlCache } from '../Helpers/diskContentUrlCache'
import { parseCsv } from '../Helpers/diskCsv'
import { diskErrorText } from '../Helpers/diskErrors'
import { adjacentFiles } from '../Helpers/diskViewerNav'
import { diskViewerMode } from '../Helpers/diskViewerMode'
import { decodeText, formatJson } from '../Helpers/diskViewerText'
import { GetDiskContentUrlTask } from '../Tasks/GetDiskContentUrlTask'
import { LoadDiskArchiveTask } from '../Tasks/LoadDiskArchiveTask'
import { LoadDiskFolderItemsTask } from '../Tasks/LoadDiskFolderItemsTask'
import { OpenDiskOfficeSessionTask } from '../Tasks/OpenDiskOfficeSessionTask'
import { DiskArchiveStore } from './DiskArchiveStore'
import { DiskNodeInfoStore } from './DiskNodeInfoStore'
import { DiskOfficeStore } from './DiskOfficeStore'

import type { DiskNode } from '../Contracts/DiskNode'
import type { CsvTable } from '../Helpers/diskCsv'
import type { DiskAdjacentFiles, DiskViewerSibling } from '../Helpers/diskViewerNav'
import type { DiskViewerMode } from '../Helpers/diskViewerMode'

/** Скільки рядків CSV малюємо таблицею — більше гальмує сторінку. */
export const CSV_PREVIEW_MAX_ROWS = 1000

export type DiskTextPreview =
    | { mode: 'text'; text: string }
    | { mode: 'json'; text: string; parsed: boolean }
    | { mode: 'csv'; table: CsvTable }

export interface DiskViewerOpenOptions {
    /**
     * Рядки, по яких гортати «попередній/наступний» (екран диска передає те,
     * що бачить людина). Без них або коли файла серед них немає — вміст
     * батьківської папки файлу.
     */
    siblings?: () => readonly DiskViewerSibling[]
    /**
     * Переглядачем керує адреса (`?file=` на екрані диска): перехід на інший
     * файл чи закриття — через неї. Без цього — показ одразу в сторі
     * (`openDiskFile` з інших модулів).
     */
    onNavigate?: (uuid: string | null) => void
    /**
     * Адмін-контекст (`DISK-006` §2.3): вміст — за адмінським підписаним
     * посиланням (`content_url?admin=1`), кожне відкриття бек пише в журнал
     * адміністратора. Відкриває адмін-панель диска; той самий переглядач.
     */
    admin?: boolean
}

const isForbidden = (status: number): boolean => status === 403

/** Відмова з готовим текстом для людини (вміст за підписаним посиланням — повз `restClient`). */
class DiskViewerError extends Error {
    constructor(readonly text: string) {
        super(text)
    }
}

const contentErrorText = (error: unknown): string =>
    error instanceof DiskViewerError ? error.text : diskErrorText(error, 'Не вдалося відкрити файл')

/**
 * Глобальний переглядач файлів диска (`DISK-001` §2.2, §6, наряд Д1.11):
 * один на застосунок, шар поверх будь-якого екрана (`DiskFileViewerHost` у
 * каркасі).
 *
 * - Картка й журнал — `DiskNodeInfoStore` (той самий, що й в інфопанелі).
 * - Вміст — за підписаним посиланням `content_url` без JWT: `<img>`,
 *   `<video>` і pdf.js ходять повз `restClient`. Посилання кешуються до
 *   строку мінус запас ({@see DiskContentUrlCache}); на 403 (строк минув,
 *   право забрали) — одна повторна спроба з новим посиланням.
 * - Текст, CSV і JSON до 2 МБ стор читає сам і віддає вже розібраними.
 * - ZIP — перелік записів від бека (`archive`, без розпакування).
 * - Office — сесія Collabora в режимі перегляду (`office`): помилку
 *   `edit_session` (503 — редактор недоступний, 422 — формат) показує
 *   картка з «Скачати».
 */
export class FileViewerStore {
    uuid: string | null = null
    readonly info = new DiskNodeInfoStore()
    /** Режим «архів»: перелік ZIP і пошук у ньому. */
    readonly archive = new DiskArchiveStore()
    /** Режим «office»: Collabora в режимі перегляду. */
    readonly office = new DiskOfficeStore()
    /** Бічна панель «Інформація» (властивості й журнал). */
    infoOpen = false

    /** Підписане `inline`-посилання для зображення, PDF, відео, аудіо. */
    mediaUrl: string | null = null
    isContentLoading = false
    contentError: string | null = null
    text: DiskTextPreview | null = null
    /**
     * Номер спроби показу: після відмови елемент (`<img>`, `<video>`, pdf.js)
     * створюється наново, навіть якщо нове посилання збіглося зі старим
     * (бек округлює строк до 5 хвилин).
     */
    mediaAttempt = 0

    /** Вміст батьківської папки — коли переглядач відкрили не з екрана диска. */
    private parentItems: DiskViewerSibling[] = []
    private parentLoadedFor: string | null = null

    private siblingsSource: (() => readonly DiskViewerSibling[]) | null = null
    private onNavigate: ((uuid: string | null) => void) | null = null
    /** Посилання на вміст — адмінські (`open(uuid, {admin: true})`). */
    private adminContext = false
    private mediaRetried = false
    private requestId = 0
    private readonly urls = new DiskContentUrlCache()

    constructor() {
        makeAutoObservable<
            FileViewerStore,
            | 'siblingsSource'
            | 'onNavigate'
            | 'adminContext'
            | 'mediaRetried'
            | 'requestId'
            | 'urls'
            | 'parentLoadedFor'
        >(this, {
            info: false,
            archive: false,
            office: false,
            siblingsSource: false,
            onNavigate: false,
            adminContext: false,
            mediaRetried: false,
            requestId: false,
            urls: false,
            parentLoadedFor: false,
        })
    }

    get isOpen(): boolean {
        return this.uuid !== null
    }

    get node(): DiskNode | null {
        const node = this.info.node

        return node !== null && node.uuid === this.uuid ? node : null
    }

    get mode(): DiskViewerMode | null {
        const node = this.node

        return node === null || node.type !== 'file' ? null : diskViewerMode(node)
    }

    /** Список для гортання: переданий екраном, якщо файл у ньому є, інакше — батьківська папка. */
    get siblings(): readonly DiskViewerSibling[] {
        const source = this.siblingsSource?.() ?? []
        const uuid = this.uuid

        return uuid !== null && source.some((item) => item.uuid === uuid) ? source : this.parentItems
    }

    get adjacent(): DiskAdjacentFiles {
        return adjacentFiles(this.siblings, this.uuid ?? '')
    }

    // ---- Відкриття й навігація ---------------------------------------------

    /** Відкрити файл (з екрана диска — за `?file=`, з інших модулів — `openDiskFile`). */
    open(uuid: string, options: DiskViewerOpenOptions = {}): void {
        const admin = options.admin === true
        const sameContext = this.adminContext === admin

        this.siblingsSource = options.siblings ?? null
        this.onNavigate = options.onNavigate ?? null
        this.adminContext = admin

        if (sameContext && this.uuid === uuid && (this.info.node !== null || this.info.isLoading)) return

        void this.show(uuid)
    }

    /** Перейти на інший файл: через адресу, якщо нею керують, інакше одразу. */
    go(uuid: string): void {
        if (this.onNavigate) this.onNavigate(uuid)
        else void this.show(uuid)
    }

    prev(): void {
        const target = this.adjacent.prev
        if (target !== null) this.go(target)
    }

    next(): void {
        const target = this.adjacent.next
        if (target !== null) this.go(target)
    }

    /** Закрити: якщо відкрито з адреси — прибрати `?file=`, адреса закриє решту. */
    close(): void {
        if (this.onNavigate) this.onNavigate(null)
        else this.reset()
    }

    /**
     * Екран диска більше не керує переглядачем (параметра `?file=` немає або
     * людина пішла з диска): закрити, якщо переглядач відкривала саме адреса.
     */
    detachRoute(): void {
        if (this.onNavigate !== null) this.reset()
    }

    reset(): void {
        this.requestId += 1
        this.uuid = null
        this.siblingsSource = null
        this.onNavigate = null
        this.adminContext = false
        this.infoOpen = false
        this.clearContent()
        this.info.close()
    }

    setInfoOpen(open: boolean): void {
        this.infoOpen = open
    }

    toggleInfo(): void {
        this.infoOpen = !this.infoOpen
    }

    // ---- Вміст -------------------------------------------------------------

    private clearContent(): void {
        this.mediaUrl = null
        this.text = null
        this.contentError = null
        this.isContentLoading = false
        this.mediaRetried = false
        this.archive.reset()
        this.office.reset()
    }

    private async show(uuid: string): Promise<void> {
        const id = ++this.requestId

        this.uuid = uuid
        this.clearContent()
        this.isContentLoading = true

        await this.info.open(uuid)

        if (id !== this.requestId) return

        const node = this.node

        if (node === null) {
            runInAction(() => {
                this.isContentLoading = false
            })

            return
        }

        void this.ensureParentItems(node)
        await this.loadContent(node, id)
    }

    private async loadContent(node: DiskNode, id: number): Promise<void> {
        const mode = node.type === 'file' ? diskViewerMode(node) : null

        try {
            if (mode === 'image' || mode === 'pdf' || mode === 'video' || mode === 'audio') {
                const url = await this.contentUrl(node, 'inline')

                runInAction(() => {
                    if (id === this.requestId) this.mediaUrl = url
                })
            } else if (mode === 'text' || mode === 'csv' || mode === 'json') {
                const text = await this.fetchText(node)
                const preview = this.preview(mode, text)

                runInAction(() => {
                    if (id === this.requestId) this.text = preview
                })
            } else if (mode === 'archive') {
                const listing = await LoadDiskArchiveTask(node.uuid)

                runInAction(() => {
                    if (id === this.requestId) this.archive.setListing(listing)
                })
            } else if (mode === 'office') {
                const session = await OpenDiskOfficeSessionTask(node.uuid)

                runInAction(() => {
                    if (id === this.requestId) this.office.open(session)
                })
            }
        } catch (error) {
            runInAction(() => {
                if (id === this.requestId) this.contentError = contentErrorText(error)
            })
        } finally {
            runInAction(() => {
                if (id === this.requestId) this.isContentLoading = false
            })
        }
    }

    private preview(mode: 'text' | 'csv' | 'json', text: string): DiskTextPreview {
        if (mode === 'csv') return { mode, table: parseCsv(text, CSV_PREVIEW_MAX_ROWS) }
        if (mode === 'json') {
            const json = formatJson(text)

            return { mode, text: json.text, parsed: json.ok }
        }

        return { mode, text }
    }

    /**
     * Текст за підписаним посиланням — простим `fetch` без JWT і cookie:
     * посилання саме себе засвідчує, а прострочений JWT у заголовку бек
     * відхилив би раніше, ніж подивився б на підпис.
     */
    private async fetchText(node: DiskNode): Promise<string> {
        let response = await fetch(await this.contentUrl(node, 'inline'), { credentials: 'omit' })

        if (isForbidden(response.status)) {
            this.forgetUrl(node, 'inline')
            response = await fetch(await this.contentUrl(node, 'inline'), { credentials: 'omit' })
        }

        if (!response.ok) {
            throw new DiskViewerError(
                isForbidden(response.status) ? 'Немає доступу до файлу' : 'Не вдалося завантажити вміст файлу'
            )
        }

        return decodeText(await response.arrayBuffer())
    }

    /**
     * `<img>`, `<video>` чи pdf.js не змогли прочитати вміст. Найчастіша
     * причина — посилання прострочене (вкладку лишили відкритою на годину):
     * один раз беремо нове. Якщо й воно не допомогло — показуємо помилку.
     */
    async mediaFailed(): Promise<void> {
        const node = this.node
        const id = this.requestId

        if (node === null) return
        if (this.mediaRetried) {
            this.contentError = 'Не вдалося показати файл. Спробуйте скачати його.'

            return
        }

        this.mediaRetried = true
        this.forgetUrl(node, 'inline')

        try {
            const url = await this.contentUrl(node, 'inline')

            runInAction(() => {
                if (id !== this.requestId) return
                this.mediaUrl = url
                this.mediaAttempt += 1
            })
        } catch (error) {
            runInAction(() => {
                if (id === this.requestId) this.contentError = diskErrorText(error, 'Не вдалося відкрити файл')
            })
        }
    }

    // ---- Посилання ---------------------------------------------------------

    /** Підписане посилання з кешу або нове (`GET …/content_url`). */
    async contentUrl(
        node: Pick<DiskNode, 'uuid' | 'updatedAt'>,
        disposition: 'inline' | 'attachment'
    ): Promise<string> {
        const key = this.urlKey(node, disposition)
        const cached = this.urls.get(key)

        if (cached !== null) return cached

        const fresh = await GetDiskContentUrlTask(node.uuid, disposition, { admin: this.adminContext })

        this.urls.set(key, fresh)

        return fresh.url
    }

    private forgetUrl(node: Pick<DiskNode, 'uuid' | 'updatedAt'>, disposition: 'inline' | 'attachment'): void {
        this.urls.forget(this.urlKey(node, disposition))
    }

    /** Адмінське посилання підписане інакше — у кеші окремо від звичайного. */
    private urlKey(node: Pick<DiskNode, 'uuid' | 'updatedAt'>, disposition: 'inline' | 'attachment'): string {
        return `${DiskContentUrlCache.key(node.uuid, disposition, node.updatedAt)}${this.adminContext ? '|admin' : ''}`
    }

    // ---- Сусіди -------------------------------------------------------------

    /** Вміст папки файлу — лише якщо екран не дав списку, де цей файл є. */
    private async ensureParentItems(node: DiskNode): Promise<void> {
        const parent = node.parentUuid
        const source = this.siblingsSource?.() ?? []

        if (parent === null || source.some((item) => item.uuid === node.uuid) || this.parentLoadedFor === parent) {
            return
        }

        this.parentLoadedFor = parent

        try {
            const items = await LoadDiskFolderItemsTask({ parent, sort: { field: 'name', order: 'asc' } })

            runInAction(() => {
                if (this.parentLoadedFor === parent) {
                    this.parentItems = items.items.map((item) => ({ uuid: item.uuid, type: item.type }))
                }
            })
        } catch {
            // Без сусідів переглядач працює — просто без «попередній/наступний»
            runInAction(() => {
                this.parentLoadedFor = null
                this.parentItems = []
            })
        }
    }
}

export const fileViewerStore = new FileViewerStore()
